import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useCustomTexts } from '../../hooks/useCustomTexts';
import { useQuickActions } from '../../hooks/useQuickActions';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';

export function LanguageSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useThemeSettings();
  const { settings, setCurrentLanguage } = useMultilingualSettings();
  const { setTexts } = useCustomTexts();
  const { setQuickActions } = useQuickActions();
  const { chatbotPublicId } = useChatbot();
  const isDark = theme.mode === 'dark';

  // Ne pas afficher le sélecteur si le support multilingue n'est pas activé
  if (!settings.enabled) {
    return null;
  }

  // Filtrer les langues activées
  const availableLanguages = settings.languages.filter(
    lang => settings.activeLanguages.includes(lang.code) || lang.code === settings.defaultLanguage
  );

  // Si une seule langue est disponible, ne pas afficher le sélecteur
  if (availableLanguages.length <= 1) {
    return null;
  }

  // Trouver la langue actuellement sélectionnée
  const currentLanguage = settings.languages.find(lang => lang.code === settings.currentLanguage) || 
                         settings.languages.find(lang => lang.code === settings.defaultLanguage);

  // Fonction pour appliquer une langue à l'interface
  const handleLanguageChange = async (lang: typeof settings.languages[0]) => {
    if (!chatbotPublicId) return;
    
    // Récupérer les traductions pour cette langue
    const translations = settings.translations?.[lang.code];
    if (translations && translations.texts) {
      // Mettre à jour les textes avec les traductions
      setTexts({
        title: translations.texts.title,
        subtitle: translations.texts.subtitle,
        welcomeMessage: translations.texts.welcomeMessage,
        inputPlaceholder: translations.texts.inputPlaceholder
      });

      // Mettre à jour les actions rapides
      if (translations.quickActions) {
        setQuickActions(translations.quickActions.map(text => ({ text })));
      }

      // Mettre à jour les labels des mini-apps
      if (translations.miniApps && chatbotPublicId) {
        // Essayer d'appliquer des traductions pour les mini-apps, mais nous avons des problèmes de typage ici
        // Cette section nécessite des modifications plus importantes de l'interface TranslatedContent
        console.log('Mini-apps translations need to be updated:', translations.miniApps);
      }
    }

    // Mettre à jour la langue courante
    await setCurrentLanguage(lang.code, chatbotPublicId);
    setIsOpen(false);
  };

  return (
    <div className="relative group z-50">
      <button 
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          isDark 
            ? 'hover:bg-white/5 text-gray-300' 
            : 'hover:bg-black/5 text-gray-700'
        }`}
      >
        <span className="text-lg">{currentLanguage?.flag}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          <div 
            className={`absolute right-0 mt-1 py-1 w-48 rounded-lg border shadow-lg z-50 ${
              isDark 
                ? 'bg-gray-800/95 border-white/10' 
                : 'bg-white border-gray-200'
            }`}
          >
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                  lang.code === settings.currentLanguage
                    ? 'bg-indigo-500/20 text-indigo-400'
                    : isDark
                      ? 'text-gray-300 hover:bg-white/5'
                      : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}